/**
 * Travel Guide Container Component
 * Container component with business logic for travel guide tab
 * Reads data from Zustand store (no caching)
 */

import React from 'react';
import { ChatLocation } from '@/types/chatTypes';
import { TravelGuidePresenter } from './TravelGuidePresenter';
import { useQuickPeekStore } from '@/store/overViewStore';

export interface TravelGuideContainerProps {
  placeName: string | null;
  sessionId: string | null;
  userId: string | null;
  isQuickPeekLoading?: boolean;
  location?: ChatLocation;
}

export const TravelGuideContainer: React.FC<TravelGuideContainerProps> = ({
  placeName,
  sessionId,
  userId,
  isQuickPeekLoading = false,
  location,
}) => {
  // Read quick peek data and loading states directly from Zustand store
  const quickPeekResponse = useQuickPeekStore(state => state.quickPeekResponse);
  const isLoadingTravelPlanning = useQuickPeekStore(
    state => state.isLoadingTravelPlanning
  );
  const isLoadingTravelPlanningOnly = useQuickPeekStore(
    state => state.isLoadingTravelPlanningOnly
  );
  const isLoadingNavigationAccessibility = useQuickPeekStore(
    state => state.isLoadingNavigationAccessibility
  );
  const isLoadingCulturalInfo = useQuickPeekStore(
    state => state.isLoadingCulturalInfo
  );
  const isLoadingActivitiesFacilities = useQuickPeekStore(
    state => state.isLoadingActivitiesFacilities
  );

  // Determine if we're loading
  // Show loader only if:
  // 1. API is actively loading (isQuickPeekLoading is true)
  // AND
  // 2. We don't have data in the store yet (quickPeekResponse is null)
  // This prevents showing loader when API completes but store hasn't updated yet
  // Also prevents showing loader indefinitely if API fails silently
  const isLoading = isQuickPeekLoading && !quickPeekResponse;

  console.log('🔍 [TravelGuideContainer] 🔄 RENDER - Full State Check:', {
    placeName,
    sessionId,
    userId,
    isQuickPeekLoading,
    hasQuickPeekResponse: !!quickPeekResponse,
    quickPeekResponseType: typeof quickPeekResponse,
    quickPeekResponseValue: quickPeekResponse,
    quickPeekKeys: quickPeekResponse ? Object.keys(quickPeekResponse) : [],
    isLoading,
    isLoadingReason: isLoading ? 'API is actively loading' : 'Not loading',
    willShowLoader: isLoading,
    willShowContent: !isLoading && !!quickPeekResponse,
    willShowNull: !isLoading && !quickPeekResponse,
    timestamp: new Date().toISOString(),
  });

  // Track store changes
  React.useEffect(() => {
    console.log(
      '🔍 [TravelGuideContainer] 📊 useEffect - Store State Changed:',
      {
        placeName,
        hasQuickPeekResponse: !!quickPeekResponse,
        quickPeekResponseKeys: quickPeekResponse
          ? Object.keys(quickPeekResponse)
          : [],
        isQuickPeekLoading,
        isLoading,
        timestamp: new Date().toISOString(),
      }
    );
  }, [placeName, quickPeekResponse, isQuickPeekLoading, isLoading]);

  return (
    <TravelGuidePresenter
      quickPeek={quickPeekResponse}
      isLoading={isLoading}
      isLoadingTravelPlanning={isLoadingTravelPlanning}
      isLoadingTravelPlanningOnly={isLoadingTravelPlanningOnly}
      isLoadingNavigationAccessibility={isLoadingNavigationAccessibility}
      isLoadingCulturalInfo={isLoadingCulturalInfo}
      isLoadingActivitiesFacilities={isLoadingActivitiesFacilities}
      placeName={placeName}
      location={location}
    />
  );
};
